import { HttpEventType } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { ToastrService } from 'ngx-toastr';
import { lastValueFrom } from 'rxjs';
import { CreateContactDto } from 'src/app/components/user-dashboard/contacts/create/dto/create-contact.dto';
import { GlobalWizardSettings } from 'src/app/components/user-dashboard/portal/dto/portal.input';
import { WebsiteIdentity } from 'src/app/components/user-dashboard/portal/portal.model';

import {
  WizardModel,
  WizardStepStatusEnum,
  StepVisibilityEnum,
  WizardTypeEnum,
} from 'src/app/models/wizard';
import { ApiService, MediaUploadService, CourseAiFieldHistoryService } from 'src/app/shared/services';
import { isAccountSetupStepsDone } from 'src/app/shared/store/_features/account-setup/account-setup.selector';
import {
  NewContactActions,
  UpdateContactActions,
} from 'src/app/shared/store/_features/contacts/contacts.actions';
import { OnboardingActions } from 'src/app/shared/store/_features/user/auth/auth.actions';
import { updateWebsiteIdentity } from 'src/app/shared/store/_features/website/website.actions';
import { COLOR_PALETTES } from './steps/hub-branding-step/color-palette-selector/color-palette-selector.component';
import { environment } from 'src/environments/environment';

export type WebsiteIdeaStepKey = 'idea' | 'audience' | 'goals';
export enum WebsiteIdeaStepStatusEnum {
  done = 'done',
  inProgress = 'inProgress',
  notStarted = 'notStarted',
}

interface WebsiteIdeaStepConfig {
  key: WebsiteIdeaStepKey;
  label: string;
  status: WebsiteIdeaStepStatusEnum;
}

export enum OnboardingWizardSetupStepsEnum {
  welcome = 'welcome',
  hub_vision = 'hub_vision',

  hub_idea_1 = 'hub_idea_1',
  hub_idea_2 = 'hub_idea_2',
  hub_idea_3 = 'hub_idea_3',

  hub_identity = 'hub_identity',
  hub_branding = 'hub_branding',
  hub_instructor = 'hub_instructor',
  hub_business_legal = 'hub_business_legal',
  hub_home_banner = 'hub_home_banner',
  completion = 'completion',
  // logo_setup = 'logo_setup',
  // theme_colors = 'theme_colors',
  instructor_profile = 'instructor_profile',
  instructor_bio = 'instructor_bio',
  business_legal = 'business_legal',
  home_banner = 'home_banner',
  finalizing = 'finalizing',
}

@Injectable({
  providedIn: 'root',
})
export class AccountSetupHelperService {
  baseGradientColors: string[] = [
    'rgba(20, 186, 203, 0.5)',
    'rgba(57, 122, 241, 0.83)',
    'rgba(176, 102, 205, 0.93)',
    'rgba(247, 82, 120, 0.5)',
    'rgba(245, 95, 92, 0.5)',
  ];
  animationMode: 'thinking' | 'writing' | 'idle' = 'thinking';
  aiLoaderMessage: string = '';

  wizardSettings: GlobalWizardSettings = {
    portalType: null,
    summaryAnimation: null,
    finalStepLabel: 'Create',
  };
  public wizardModel: WizardModel;
  wizardData = {
    hubInput: {
      aiState: {
        aiSeed: '',
        aiData: {
          idea: ``,
          audience: ``,
          goals: ``,
        },
      },
      hubIdentity: {
        name: '',
        subDomain: '',
        domain: '',
        tagline: '',
        taglinePending: false,
      },
      hubBranding: {
        colorPalette: null,
        logoMode: 'placeholder' as 'none' | 'placeholder' | 'upload',
        placeHolderLogo: null as string | null,
        logo: [],
        logo_id: [],
        logoUrl: '' as string,
      },
      hubInstructor: {
        fullName: '',
        title: '',
        email: '',
        bio: '',
        image: [],
        image_id: [],
      },
      hubBusinessLegalDetails: {
        contactEmail: '',
        businessAddress: ``,
        refundPeriod: '14',
        customDays: '',
      },
      hubHomePageBanner: {
        image: [] as any[],
        image_id: [] as number[],
        url: '',
        bannerMode: 'ai-generate' as 'upload' | 'ai-generate',
        selectedStyle: '',
        bannerPrompt: '',
        promptGenerated: false,
        generationCount: 0,
        currentHistoryIndex: -1,
        generationHistory: [] as any[],
      },
    },
    generation: {
      status: 'idle' as 'idle' | 'processing' | 'completed' | 'failed',
      startedAt: null as number | null,
      identitySubmitted: false,
    },

    // hubInput: {
    //   aiState: {
    //     aiSeed: "Teaching",
    //     aiData: {
    //       idea: "The Teaching Hub is an online academy that offers a wide range of courses, from basic to advanced, in various subjects like mathematics, science, literature, and art, all taught by expert educators. Visitors can also find a forum where they can connect with fellow learners and teachers to exchange ideas and solutions.",
    //       audience: "The ideal target learners for a teaching hub would be individuals aged 18 to 35, with a diverse educational and professional background, who are eager to enhance their teaching skills, incorporate innovative pedagogical techniques, and face challenges such as adapting to different learning styles and integrating technology into the classroom.",
    //       goals: "The main objective of a hub focused on teaching is to provide learners with a comprehensive and versatile learning experience. This can be achieved by offering a wide range of educational resources, such as videos, articles, and interactive quizzes, designed to cater to different learning styles and preferences. By doing so, the hub aims to make learning accessible and engaging for all students, regardless of their background or skill level. In addition to providing educational content, the hub should also foster a sense of community among learners. By encouraging discussion, collaboration, and peer-to-peer support, the hub can help learners develop essential social and communication skills, which are crucial for success in both personal and professional contexts. Ultimately, the value of this hub lies in its ability to empower learners with the knowledge, skills, and confidence needed to achieve their full potential and make a positive impact on the world."
    //     }
    //   },
    //   hubIdentity: {
    //     name: "TeachInnovate",
    //     subDomain: "teachinnovate",
    //     domain: "teachinnovate.learmosites.dev",
    //     tagline: "Empowering Educators to Transform Learning"
    //   },
    //   hubBranding: {
    //     colorPalette: {
    //       id: 2,
    //       name: "Ocean Depths",
    //       colors: [
    //         "#B2F5EA",
    //         "#81E6D9",
    //         "#4FD1C5",
    //         "#38B2AC",
    //         "#285E61"
    //       ]
    //     },
    //     logoMode: "placeholder",
    //     logo: [],
    //     logo_id: []
    //   },
    //   hubInstructor: {
    //     fullName: "hello emy",
    //     title: "Instructor",
    //     bio: "Hello Emy is an enthusiastic and dedicated instructor at the hub. With a passion for teaching and a deep understanding of the subject matter, she creates an engaging and supportive learning environment for her students. Her ability to explain complex concepts in a clear and concise manner has made her a favorite among both seasoned learners and beginners.",
    //     image: [],
    //     image_id: []
    //   },
    //   hubBusinessLegalDetails: {
    //     contactEmail: "contact@learmosites.dev",
    //     businessAddress: "12 El-Mansoura St, Giza, Egypt",
    //     refundPeriod: "7",
    //     customDays: ""
    //   },
    //   hubHomePageBanner: {
    //     image: [],
    //     image_id: []
    //   }
    // },
    temp: new Object(),
  };
  accountSetupData: any = {
    vision: null,
  };

  readonly WebsiteIdeaSteps: WebsiteIdeaStepConfig[] = [
    {
      key: 'idea',
      label: 'Idea',
      status: WebsiteIdeaStepStatusEnum.notStarted,
    },
    {
      key: 'audience',
      label: 'Audience',
      status: WebsiteIdeaStepStatusEnum.notStarted,
    },
    {
      key: 'goals',
      label: 'Goals',
      status: WebsiteIdeaStepStatusEnum.notStarted,
    },
  ];

  ownerContactId: number;

  constructor(
    private apiService: ApiService,
    private store: Store,
    private mediaUpload: MediaUploadService,
    private toastr: ToastrService,
    private historyService: CourseAiFieldHistoryService,
  ) {
    this.fetchOwnerContact();
  }

  fetchOwnerContact() {
    this.apiService
      .getContacts({ dummy: false, type: 'owner' })
      .subscribe((contacts: any[]) => {
        if (contacts && contacts.length > 0) {
          const owner = contacts[0];
          this.ownerContactId = owner.id;
          if (!this.wizardData.hubInput.hubInstructor.fullName) {
            this.wizardData.hubInput.hubInstructor.fullName = owner.name;
          }
          // If we wanted to pre-fill other fields like bio/title if they exist on the owner contact, we could do it here
        }
      });
  }

  restoreInstructorImageById(imageId: number): void {
    if (!imageId) return;
    this.apiService.getMediaById(imageId).subscribe({
      next: (media: any) => {
        if (
          media &&
          (!this.wizardData.hubInput.hubInstructor.image ||
            !this.wizardData.hubInput.hubInstructor.image.length)
        ) {
          this.wizardData.hubInput.hubInstructor.image = [media];
        }
      },
      error: () => {},
    });
  }

  changeColorsForDuration(colors: string[], duration = 2500) {
    const tempDefaultValue = [...this.baseGradientColors];
    this.baseGradientColors = colors;
    setTimeout(() => {
      this.baseGradientColors = tempDefaultValue;
    }, duration);
  }

  setAiLoaderState(
    animationMode: 'thinking' | 'writing' | 'idle' = 'thinking',
  ) {
    this.animationMode = animationMode;
  }

  setAiLoaderMessage(message: string, displayFor: number = 0) {
    this.aiLoaderMessage = message;
    if (displayFor > 0) {
      setTimeout(() => {
        this.aiLoaderMessage = '';
      }, displayFor);
    }
  }

  // Wizard Initial
  initializeWizardProductCreate() {
    this.historyService.clearHistory();
    this.wizardSettings.finalStepLabel = 'Create My Product';
    this.wizardModel = new WizardModel(
      [
        {
          id: 1,
          title: 'Welcome',
          description: 'Welcome to Learmo',
          status: WizardStepStatusEnum.none,
          enumTag: OnboardingWizardSetupStepsEnum.welcome,
          visibility: StepVisibilityEnum.visible,
          isValid: true,
          hideButtons: {
            back: true,
            next: false,
          },
          hideSteps: true,
          navigationAction: () => {
            this.navigationAction();
          },
          nextButtonLabel: 'Choose to continue',
        },
        {
          id: 2,
          title: 'Hub Vision',
          description: 'Describe your hub',
          status: WizardStepStatusEnum.dummy,
          visibility: StepVisibilityEnum.visible,
          isValid: false,
          validate: this.hubVisionValidator,
          enumTag: OnboardingWizardSetupStepsEnum.hub_vision,
          hideButtons: {
            back: false,
            next: false,
          },
          navigationAction: () => {
            this.navigationAction();
          },
        },
        {
          id: 31,
          title: 'Hub Idea 1 (Idea',
          description: 'Ai generated',
          status: WizardStepStatusEnum.dummy,
          visibility: StepVisibilityEnum.visible,
          isValid: false,
          validate: this.hubIdeaValidator,
          enumTag: OnboardingWizardSetupStepsEnum.hub_idea_1,
          hideButtons: {
            back: false,
            next: false,
          },
          navigationAction: () => {
            this.navigationAction();
          },
        },
        {
          id: 32,
          title: 'Hub Idea 2 (Audience)',
          description: 'Ai generated',
          status: WizardStepStatusEnum.dummy,
          visibility: StepVisibilityEnum.visible,
          isValid: false,
          validate: this.hubAudienceValidator,
          enumTag: OnboardingWizardSetupStepsEnum.hub_idea_2,
          hideButtons: {
            back: false,
            next: false,
          },
          navigationAction: () => {
            this.navigationAction();
          },
        },
        {
          id: 33,
          title: 'Hub Idea 3 (Goals)',
          description: 'Ai generated',
          status: WizardStepStatusEnum.dummy,
          visibility: StepVisibilityEnum.visible,
          isValid: false,
          validate: this.hubGoalsValidator,
          enumTag: OnboardingWizardSetupStepsEnum.hub_idea_3,
          hideButtons: {
            back: false,
            next: false,
          },
          navigationAction: () => {
            this.navigationAction();
          },
        },
        {
          id: 4,
          title: 'Hub Identity',
          description: 'Ai generated',
          status: WizardStepStatusEnum.dummy,
          visibility: StepVisibilityEnum.visible,
          isValid: false,
          validate: this.hubIdentityValidator,
          enumTag: OnboardingWizardSetupStepsEnum.hub_identity,
          hideButtons: {
            back: false,
            next: false,
          },
          navigationAction: () => {
            this.navigationAction();
          },
        },
        {
          id: 5,
          title: 'Hub Branding',
          description: 'Ai generated',
          status: WizardStepStatusEnum.dummy,
          visibility: StepVisibilityEnum.visible,
          isValid: false,
          validate: this.hubBrandingValidator,
          enumTag: OnboardingWizardSetupStepsEnum.hub_branding,
          hideButtons: {
            back: false,
            next: false,
          },
          navigationAction: () => {
            this.navigationAction();
          },
        },
        {
          id: 6,
          title: 'Hub Instructors',
          description: 'Ai generated',
          status: WizardStepStatusEnum.dummy,
          visibility: StepVisibilityEnum.visible,
          isValid: true,
          enumTag: OnboardingWizardSetupStepsEnum.hub_instructor,
          hideButtons: {
            back: false,
            next: false,
          },
          navigationAction: () => {
            this.navigationAction();
          },
        },
        {
          id: 7,
          title: 'Hub Legal Business Details',
          description: 'Ai generated',
          status: WizardStepStatusEnum.dummy,
          visibility: StepVisibilityEnum.visible,
          isValid: true,
          enumTag: OnboardingWizardSetupStepsEnum.hub_business_legal,
          hideButtons: {
            back: false,
            next: false,
          },
          navigationAction: () => {
            this.navigationAction();
          },
        },
        {
          id: 8,
          title: 'Hub Home Banner',
          description: 'Ai generated',
          status: WizardStepStatusEnum.dummy,
          visibility: StepVisibilityEnum.visible,
          isValid: true,
          enumTag: OnboardingWizardSetupStepsEnum.hub_home_banner,
          nextButtonLabel: 'Finish',
          hideButtons: {
            back: false,
            next: false,
          },
          navigationAction: () => {
            this.navigationAction();
          },
        },
        {
          id: 9,
          title: 'Completion',
          description: 'Ai generated',
          status: WizardStepStatusEnum.dummy,
          visibility: StepVisibilityEnum.visible,
          isValid: true,
          enumTag: OnboardingWizardSetupStepsEnum.completion,
          hideButtons: {
            back: true,
            next: true,
          },
          navigationAction: () => {
            this.navigationAction();
          },
        },
      ],
      0,
      WizardTypeEnum.accountSetup,
      this.backAction,
      this.completeAccountSetup,
    );
    //
  }

  hubVisionValidator = () => {
    return !!this.wizardData?.hubInput?.aiState?.aiSeed;
  };

  hubIdeaValidator = () => {
    return !!this.wizardData?.hubInput?.aiState?.aiData?.idea;
  };

  hubAudienceValidator = () => {
    return !!this.wizardData?.hubInput?.aiState?.aiData?.audience;
  };

  hubGoalsValidator = () => {
    return !!this.wizardData?.hubInput?.aiState?.aiData?.goals;
  };

  hubIdentityValidator = () => {
    return (
      !!this.wizardData?.hubInput?.hubIdentity?.name &&
      !!this.wizardData?.hubInput?.hubIdentity?.subDomain &&
      !!this.wizardData?.hubInput?.hubIdentity?.tagline
    );
  };

  hubBrandingValidator = () => {
    return !!this.wizardData?.hubInput?.hubBranding?.colorPalette;
  };

  navigationAction = () => {};

  backAction = () => {
    // this.router.navigate(['dashboard', 'product']);
  };

  private _isCompletedSuccessfully = false;

  completeAccountSetup = async (): Promise<void> => {
    if (this._isCompletedSuccessfully) return;
    // 1. Immediately cancel pending debounced autosaves and block any further saves
    this._isSetupCompletedOrCompleting = true;
    clearTimeout(this._saveDebounceTimer);

    try {
      // 2. Await backend call so we only delete progress if setup actually succeeds
      await lastValueFrom(this.apiService.markWebsiteOnboarding(true));
      this._isCompletedSuccessfully = true;
      this.store.dispatch(OnboardingActions.updateSuccess({ isComplete: true }));

      // 3. Delete progress record only AFTER successful markWebsiteOnboarding
      this.clearSavedProgress();
    } catch (err) {
      console.error('[AccountSetup] markWebsiteOnboarding failed:', err);
      // Keep progress intact so user can resume / retry
      this._isSetupCompletedOrCompleting = false;
      this.store.dispatch(OnboardingActions.updateFailure({ error: err }));
      throw err;
    }
  };

  /** Generate a canvas-based placeholder logo PNG as a base64 data URL. Pure client-side, no AI/API calls. */
  generatePlaceholderLogo(
    hubName?: string,
    palette?: { colors: string[] } | null,
  ): string | null {
    try {
      const name = (hubName ?? this.wizardData.hubInput.hubIdentity.name)?.trim() || 'Hub';
      const initial = name[0].toUpperCase();
      const colors = palette?.colors?.length
        ? palette.colors
        : (this.wizardData.hubInput.hubBranding.colorPalette?.colors?.length
            ? this.wizardData.hubInput.hubBranding.colorPalette.colors
            : ['#F5F7FA', '#E4E7EB', '#CBD2D9', '#9AA5B1', '#7B8794']);
      const iconSize = 128;
      const padding = 24;
      const fontSize = 64;
      const textWidth = name.length * (fontSize * 0.6);
      const canvasWidth = iconSize + padding + textWidth;
      const canvas = document.createElement('canvas');
      canvas.width = canvasWidth;
      canvas.height = iconSize;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      const gradient = ctx.createLinearGradient(0, 0, iconSize, iconSize);
      gradient.addColorStop(0, colors[0]);
      gradient.addColorStop(1, colors[4] ?? colors[0]);
      ctx.fillStyle = gradient;
      const r = 24;
      ctx.beginPath();
      ctx.moveTo(r, 0); ctx.lineTo(iconSize - r, 0); ctx.quadraticCurveTo(iconSize, 0, iconSize, r);
      ctx.lineTo(iconSize, iconSize - r); ctx.quadraticCurveTo(iconSize, iconSize, iconSize - r, iconSize);
      ctx.lineTo(r, iconSize); ctx.quadraticCurveTo(0, iconSize, 0, iconSize - r);
      ctx.lineTo(0, r); ctx.quadraticCurveTo(0, 0, r, 0);
      ctx.closePath(); ctx.fill();
      ctx.fillStyle = '#fff';
      ctx.font = 'bold 72px Inter, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(initial, iconSize / 2, iconSize / 2 + 4);
      ctx.fillStyle = colors[4] ?? colors[0];
      ctx.font = 'bold 64px Inter, sans-serif';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(name, iconSize + padding, iconSize / 2 + 4);
      return canvas.toDataURL('image/png');
    } catch (e) {
      console.warn('[AccountSetup] generatePlaceholderLogo failed', e);
      return null;
    }
  }

  async uploadLogo(base64Image: string): Promise<any> {
    const file = this.mediaUpload.base64ToFile(base64Image, 'site-logo.png');

    return new Promise((resolve, reject) => {
      this.mediaUpload.uploadFile(file, 'media').subscribe({
        next: (event) => {
          if (event.type === HttpEventType.Response) {
            this.wizardData.hubInput.hubBranding.logo_id =
              event.body.uploadedFiles.map((file) => file.id);
            this.scheduleSaveProgress();
            resolve(1);
          }
        },
        error: () => {
          this.toastr.error('Logo Upload failed.');
          reject();
        },
      });
    });
  }
  // Wizard Final Actions
  updateWebsiteIdentity = async () => {
    if (
      this.wizardData.hubInput.hubBranding.logoMode === 'placeholder' &&
      this.wizardData.hubInput.hubBranding.placeHolderLogo &&
      this.wizardData.hubInput.hubBranding.logo_id.length === 0
    ) {
      await this.uploadLogo(
        this.wizardData.hubInput.hubBranding.placeHolderLogo,
      );
    }
    this.wizardData.hubInput;
    const payload: WebsiteIdentity = {
      title: this.wizardData.hubInput.hubIdentity.name,
      description: this.wizardData.hubInput.hubIdentity.tagline,
      logo_id:
        this.wizardData.hubInput.hubBranding.logo_id.length > 0
          ? this.wizardData.hubInput.hubBranding.logo_id
          : undefined,
      color_palette: this.wizardData.hubInput.hubBranding.colorPalette,
      legal_business_details: this.wizardData.hubInput.hubBusinessLegalDetails,
      ai_state: this.wizardData.hubInput.aiState,
      label: this.wizardData.hubInput.hubIdentity.name,
      page_initial_data: {
        homeBanner: {
          image_id: this.wizardData.hubInput.hubHomePageBanner.image_id,
        },
      },
    };

    let instructorPayload: CreateContactDto = {
      name: this.wizardData.hubInput.hubInstructor.fullName,
      title: this.wizardData.hubInput.hubInstructor.title,
      bio: this.wizardData.hubInput.hubInstructor.bio,
      type: 'instructor',
      avatar:
        this.wizardData.hubInput.hubInstructor.image_id.length > 0
          ? this.wizardData.hubInput.hubInstructor.image_id[0]
          : undefined,
    };

    this.store.dispatch(updateWebsiteIdentity.update({ identity: payload }));

    if (this.ownerContactId) {
      this.store.dispatch(
        UpdateContactActions.update({
          itemId: this.ownerContactId,
          item: instructorPayload,
          contactType: 'owner',
        }),
      );
    } else {
      this.store.dispatch(
        NewContactActions.create({ createPayload: instructorPayload }),
      );
    }

    this.store.dispatch(
      updateWebsiteIdentity.updateSubDomain({
        domain: this.wizardData.hubInput.hubIdentity.subDomain,
        silent: true,
      }),
    );

    // Explicitly await both HTTP requests to ensure they are saved before generation begins
    const subDomain = (
      this.wizardData.hubInput.hubIdentity.subDomain || ''
    )
      .trim()
      .toLowerCase();
    try {
      const [identityRes, subDomainRes]: [any, any] = await Promise.all([
        lastValueFrom(this.apiService.setWebsitedentity(payload)),
        lastValueFrom(this.apiService.changePortalSubdomain(subDomain)),
      ]);
      if (subDomainRes && subDomainRes.success === false) {
        throw new Error(subDomainRes.error || 'Failed to update subdomain');
      }
      console.log('[AccountSetup] Identity and subdomain saved successfully', {
        identityRes,
        subDomainRes,
      });
    } catch (err) {
      console.error('[AccountSetup] Error saving identity or subdomain:', err);
      throw err;
    }

    // this.loadingCreate = true;
    // this.store.select(selectProductsState).pipe(takeWhile(state => state?.createNewProductProcess.status !== 'done' && state?.createNewProductProcess.status !== 'error', true)).subscribe(state => {
    //   switch (state?.createNewProductProcess.status) {
    //     case 'done':
    //       this.onProductCreateDone(state.createNewProductProcess.lastCreatedProduct);
    //       break;
    //     case 'error':
    //       this.onProductCreateError(state.createNewProductProcess.error)
    //       break;
    //     default:
    //       break;
    //   }

    // })
  };

  // ─────────────────────────────────────────────────────────────────
  // Onboarding Progress Persistence
  // ─────────────────────────────────────────────────────────────────

  /** Debounce handle for the 800 ms autosave timer. */
  private _saveDebounceTimer: any;
  /** Monotonically increasing version — incremented on every save. */
  private _progressVersion = 0;
  /** Set to true when completion starts — blocks any further autosaves. */
  private _isSetupCompletedOrCompleting = false;

  /**
   * Build the safe subset of wizardData to persist.
   * Excludes raw binary blobs (image[], logo[]) — stores IDs/references only.
   * Excludes sensitive fields (passwords, payment info, API keys).
   */
  private _buildProgressPayload(stepIndex: number, stepTag: string): object {
    const h = this.wizardData.hubInput;
    return {
      version: ++this._progressVersion,
      updated_at: Math.floor(Date.now() / 1000),
      currentStepIndex: stepIndex,
      currentStepTag: stepTag,
      hubInput: {
        aiState: h.aiState,
        hubIdentity: h.hubIdentity
          ? {
              ...h.hubIdentity,
              domain: (h.hubIdentity.domain || '').trim().toLowerCase(),
              subDomain: (h.hubIdentity.subDomain || '').trim().toLowerCase(),
            }
          : h.hubIdentity,
        hubBranding: {
          colorPalette: h.hubBranding.colorPalette,
          logoMode: h.hubBranding.logoMode,
          logo_id: h.hubBranding.logo_id,          // IDs only — no raw blobs
          logoUrl: (h.hubBranding.logoUrl && !h.hubBranding.logoUrl.startsWith('data:')) ? h.hubBranding.logoUrl : '',
        },
        hubInstructor: {
          fullName: h.hubInstructor.fullName,
          title:    h.hubInstructor.title,
          email:    h.hubInstructor.email,
          bio:      h.hubInstructor.bio,
          image_id: h.hubInstructor.image_id,
          image:    h.hubInstructor.image,
        },
        hubBusinessLegalDetails: h.hubBusinessLegalDetails,
        hubHomePageBanner: {
          image_id:            h.hubHomePageBanner.image_id,
          url:                 (h.hubHomePageBanner.url && !h.hubHomePageBanner.url.startsWith('data:')) ? h.hubHomePageBanner.url : '',
          bannerMode:          h.hubHomePageBanner.bannerMode || 'ai-generate',
          selectedStyle:       h.hubHomePageBanner.selectedStyle || '',
          bannerPrompt:        h.hubHomePageBanner.bannerPrompt || '',
          promptGenerated:     !!h.hubHomePageBanner.promptGenerated,
          generationCount:     h.hubHomePageBanner.generationCount ?? 0,
          currentHistoryIndex: h.hubHomePageBanner.currentHistoryIndex ?? -1,
          image:               h.hubHomePageBanner.image || [],
        },
      },
      generation: {
        status: this.wizardData.generation.status,
        startedAt: this.wizardData.generation.startedAt,
        identitySubmitted: !!this.wizardData.generation.identitySubmitted,
      },
    };
  }

  /**
   * Debounced autosave — fires 800 ms after last call.
   * Use for form-input changes.
   */
  scheduleSaveProgress(): void {
    if (this._isSetupCompletedOrCompleting) return;
    clearTimeout(this._saveDebounceTimer);
    this._saveDebounceTimer = setTimeout(() => this._doSave(), 800);
  }

  /**
   * Immediate save — cancels any pending debounce and saves synchronously.
   * Use on step navigation and in the window `beforeunload` handler.
   */
  flushSaveProgress(): void {
    if (this._isSetupCompletedOrCompleting) return;
    clearTimeout(this._saveDebounceTimer);
    this._doSave();
  }

  /**
   * Asynchronous immediate save that returns a Promise and awaits completion.
   * Used before generation starts to guarantee persistence before any downstream HTTP requests.
   */
  async saveProgressAsync(customStepIndex?: number, customStepTag?: string): Promise<any> {
    if (this._isSetupCompletedOrCompleting) return;
    clearTimeout(this._saveDebounceTimer);
    const stepIndex = customStepIndex ?? this.wizardModel?.currentStep?.index ?? 0;
    const stepTag   = customStepTag ?? this.wizardModel?.currentStep?.step?.enumTag ?? '';
    const payload   = this._buildProgressPayload(stepIndex, stepTag);
    try {
      return await lastValueFrom(this.apiService.saveOnboardingProgress(payload));
    } catch (err) {
      console.warn('[AccountSetup] saveProgressAsync fallback save:', err);
    }
  }

  /**
   * Sends the progress via `fetch keepalive` so it survives page unload.
   * Falls back to the Angular HTTP client on normal saves or non-2xx responses.
   */
  private _doSave(): void {
    if (this._isSetupCompletedOrCompleting) return;
    const stepIndex = this.wizardModel?.currentStep?.index ?? 0;
    const stepTag   = this.wizardModel?.currentStep?.step?.enumTag ?? '';
    const payload   = this._buildProgressPayload(stepIndex, stepTag);

    // Resolve API URL and auth token from environment and localStorage
    const apiBase: string =
      this.apiService.apiUrl || environment.api?.url || '';
    const tokenKey = environment.storageKeys?.tokenStorageKey || 'token';
    const token: string = localStorage.getItem(tokenKey) || '';
    const url = apiBase
      ? `${apiBase.replace(/\/$/, '')}/users/onboarding-progress`
      : '';

    const body = JSON.stringify({ progress: JSON.stringify(payload) });
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    if (url) {
      try {
        fetch(url, { method: 'PUT', keepalive: true, headers, body })
          .then((res) => {
            if (!res.ok) {
              console.warn(
                `[Keepalive] Received non-2xx (${res.status}), falling back to Angular HTTP save`,
              );
              this.apiService.saveOnboardingProgress(payload).subscribe();
            }
          })
          .catch(() => {
            this.apiService.saveOnboardingProgress(payload).subscribe();
          });
        return;
      } catch {
        /* fall through to Angular HTTP */
      }
    }
    this.apiService.saveOnboardingProgress(payload).subscribe();
  }

  /**
   * Restores saved progress on wizard initialisation.
   * Returns the saved step index, or -1 if no saved progress.
   */
  async restoreProgress(): Promise<number> {
    try {
      const result: { progress: string } | null = await lastValueFrom(
        this.apiService.getOnboardingProgress(),
      );
      if (!result?.progress) return -1;

      type Saved = {
        version?: number;
        updated_at?: number;
        currentStepIndex?: number;
        currentStepTag?: string;
        hubInput?: any;
        generation?: typeof this.wizardData.generation;
        cleared?: boolean;
      };

      const saved: Saved = JSON.parse(result.progress);
      if (saved?.cleared || typeof saved?.currentStepIndex !== 'number') return -1;

      // Deep-merge saved hubInput fields (text/IDs only)
      const h = saved.hubInput ?? {};
      if (h.aiState)
        this.wizardData.hubInput.aiState = { ...this.wizardData.hubInput.aiState, ...h.aiState };
      if (h.hubIdentity)
        this.wizardData.hubInput.hubIdentity = { ...this.wizardData.hubInput.hubIdentity, ...h.hubIdentity };
      if (h.hubBranding) {
        if (h.hubBranding.colorPalette != null)
          this.wizardData.hubInput.hubBranding.colorPalette = h.hubBranding.colorPalette;
        // Default to 'placeholder' if nothing was saved
        this.wizardData.hubInput.hubBranding.logoMode = h.hubBranding.logoMode ?? 'placeholder';
        if (h.hubBranding.logo_id?.length)
          this.wizardData.hubInput.hubBranding.logo_id = h.hubBranding.logo_id;
        if (h.hubBranding.logoUrl && !h.hubBranding.logoUrl.startsWith('data:'))
          this.wizardData.hubInput.hubBranding.logoUrl = h.hubBranding.logoUrl;
        // Regenerate placeholder canvas (pure client-side) so the preview is available after restore
        if (this.wizardData.hubInput.hubBranding.logoMode === 'placeholder') {
          const generated = this.generatePlaceholderLogo();
          if (generated) this.wizardData.hubInput.hubBranding.placeHolderLogo = generated;
        }
      }
      if (h.hubInstructor) {
        this.wizardData.hubInput.hubInstructor = {
          ...this.wizardData.hubInput.hubInstructor,
          fullName: h.hubInstructor.fullName ?? this.wizardData.hubInput.hubInstructor.fullName,
          title:    h.hubInstructor.title    ?? this.wizardData.hubInput.hubInstructor.title,
          email:    h.hubInstructor.email    ?? this.wizardData.hubInput.hubInstructor.email,
          bio:      h.hubInstructor.bio      ?? this.wizardData.hubInput.hubInstructor.bio,
          image_id: h.hubInstructor.image_id?.length
            ? h.hubInstructor.image_id
            : this.wizardData.hubInput.hubInstructor.image_id,
          image:    h.hubInstructor.image?.length
            ? h.hubInstructor.image
            : this.wizardData.hubInput.hubInstructor.image,
        };

        if (
          (!this.wizardData.hubInput.hubInstructor.image ||
            !this.wizardData.hubInput.hubInstructor.image.length) &&
          this.wizardData.hubInput.hubInstructor.image_id?.length
        ) {
          try {
            const media = await lastValueFrom(
              this.apiService.getMediaById(
                this.wizardData.hubInput.hubInstructor.image_id[0],
              ),
            );
            if (media) {
              this.wizardData.hubInput.hubInstructor.image = [media];
            }
          } catch (err) {
            console.warn('[AccountSetup] Could not restore instructor avatar by id:', err);
          }
        }
      }
      if (h.hubBusinessLegalDetails)
        this.wizardData.hubInput.hubBusinessLegalDetails = {
          ...this.wizardData.hubInput.hubBusinessLegalDetails,
          ...h.hubBusinessLegalDetails,
        };
      if (h.hubHomePageBanner) {
        const b = h.hubHomePageBanner;
        if (b.image_id?.length)
          this.wizardData.hubInput.hubHomePageBanner.image_id = b.image_id;
        if (b.url && !b.url.startsWith('data:'))
          this.wizardData.hubInput.hubHomePageBanner.url = b.url;
        if (b.bannerMode)
          this.wizardData.hubInput.hubHomePageBanner.bannerMode = b.bannerMode;
        if (b.selectedStyle)
          this.wizardData.hubInput.hubHomePageBanner.selectedStyle = b.selectedStyle;
        if (b.bannerPrompt)
          this.wizardData.hubInput.hubHomePageBanner.bannerPrompt = b.bannerPrompt;
        if (typeof b.promptGenerated === 'boolean')
          this.wizardData.hubInput.hubHomePageBanner.promptGenerated = b.promptGenerated;
        if (typeof b.generationCount === 'number')
          this.wizardData.hubInput.hubHomePageBanner.generationCount = b.generationCount;
        if (typeof b.currentHistoryIndex === 'number')
          this.wizardData.hubInput.hubHomePageBanner.currentHistoryIndex = b.currentHistoryIndex;
        if (b.image?.length)
          this.wizardData.hubInput.hubHomePageBanner.image = b.image;

        // Fallback: If url is missing but image/image_id exists, restore url cleanly without errors
        if (!this.wizardData.hubInput.hubHomePageBanner.url) {
          if (
            this.wizardData.hubInput.hubHomePageBanner.image?.length &&
            this.wizardData.hubInput.hubHomePageBanner.image[0]?.url
          ) {
            this.wizardData.hubInput.hubHomePageBanner.url =
              this.wizardData.hubInput.hubHomePageBanner.image[0].url;
          } else if (this.wizardData.hubInput.hubHomePageBanner.image_id?.length) {
            try {
              const media = await lastValueFrom(
                this.apiService.getMediaById(
                  this.wizardData.hubInput.hubHomePageBanner.image_id[0],
                  () => {},
                ),
              );
              if (media?.url) {
                this.wizardData.hubInput.hubHomePageBanner.url = media.url;
                this.wizardData.hubInput.hubHomePageBanner.image = [media];
              }
            } catch {
              // File missing or deleted: leave url empty so "No banner yet" shows cleanly
            }
          }
        }
      }

      // Restore generation status with stale-processing protection (10 min)
      if (saved.generation) {
        const TEN_MINUTES = 10 * 60;
        const now = Math.floor(Date.now() / 1000);
        if (
          saved.generation.status === 'processing' &&
          saved.generation.startedAt != null &&
          now - saved.generation.startedAt > TEN_MINUTES
        ) {
          saved.generation.status = 'failed';
        }
        this.wizardData.generation = {
          identitySubmitted: false,
          ...saved.generation,
        };
      }

      this._progressVersion = saved.version ?? 0;
      return saved.currentStepIndex;
    } catch {
      return -1;
    }
  }

  /** Clears the saved progress both on the server (DELETE) and writes a tombstone sentinel. */
  clearSavedProgress(): void {
    this._progressVersion++;
    this.apiService.clearOnboardingProgress().subscribe({
      next: () => {},
      error: () => {
        // Fallback: overwrite with cleared sentinel if DELETE fails
        this.apiService
          .saveOnboardingProgress({
            version: this._progressVersion,
            updated_at: Math.floor(Date.now() / 1000),
            cleared: true,
          })
          .subscribe();
      },
    });
  }
}

